import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import WorkplaceNameDisplay from '../../components/WorkplaceNameDisplay';
import { EvacuationPlanModal, EvacuationPlanData } from '../../components/EvacuationPlanModal';
import { workplaceApi, emergencyPlanApi } from '../../services/api';
import './EmergencyActionPlanPage.css';

interface Workplace {
  id: number;
  name: string;
  address?: string;
  riskLevel: 'low' | 'dangerous' | 'veryDangerous' | 'very_dangerous';
  sskRegistrationNo?: string;
  sgkNo?: string;
  city?: string;
  district?: string;
  employeeCount?: number;
  naceCode?: string;
  sector?: string;
  phone?: string;
  email?: string;
  employerName?: string;
  assignedExpert?: { firstName?: string; lastName?: string; certificateNumber?: string };
  assignedDoctor?: { firstName?: string; lastName?: string; diplomaNumber?: string };
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  phone: string;
}

interface SurroundingWorkplace {
  id: string;
  name: string;
  activity: string;
  riskEffect: string;
}

const RISK_LEVEL_LABEL: Record<string, string> = {
  low: 'Az Tehlikeli',
  dangerous: 'Tehlikeli',
  veryDangerous: 'Çok Tehlikeli',
  very_dangerous: 'Çok Tehlikeli'
};

const RISK_LEVEL_VALIDITY_YEARS: Record<string, number> = {
  low: 6,
  dangerous: 4,
  veryDangerous: 2,
  very_dangerous: 2
};

export const EmergencyActionPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const searchWrapRef = useRef<HTMLDivElement>(null);

  // Step 1: Workplace selection, Step 2: Form & Teams, Step 3: Preview/Export
  const [step, setStep] = useState<number>(1);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [selectedWorkplace, setSelectedWorkplace] = useState<Workplace | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Form Fields
  const [issueDate, setIssueDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [revisionNo, setRevisionNo] = useState<string>('00');
  const [validityDate, setValidityDate] = useState<string>('');
  const [assemblyPoint, setAssemblyPoint] = useState<string>('İşyeri Ön Otoparkı / Bahçe Alanı');
  
  // Expert & Employer Fields
  const [expertName, setExpertName] = useState<string>('');
  const [expertCertNo, setExpertCertNo] = useState<string>('');
  const [employerName, setEmployerName] = useState<string>('');
  const [employerTitle, setEmployerTitle] = useState<string>('İşveren / İşveren Vekili');

  // Surrounding Workplaces
  const [surroundings, setSurroundings] = useState<SurroundingWorkplace[]>([]);
  const [newSurrounding, setNewSurrounding] = useState({ name: '', activity: '', riskEffect: '' });

  // Teams State
  const [teams, setTeams] = useState<Record<string, TeamMember[]>>({
    sondurme: [],
    kurtarma: [],
    koruma: [],
    ilkyardim: []
  });

  const [activeTeamTab, setActiveTeamTab] = useState<'sondurme' | 'kurtarma' | 'koruma' | 'ilkyardim'>('sondurme');
  const [newMember, setNewMember] = useState({ name: '', role: 'Ekip Başı', phone: '' });

  // Inline Consent States
  const [wordConsentOpen, setWordConsentOpen] = useState<boolean>(false);
  const [wordConsentChecked, setWordConsentChecked] = useState<boolean>(false);

  const [pdfConsentOpen, setPdfConsentOpen] = useState<boolean>(false);
  const [pdfConsentChecked, setPdfConsentChecked] = useState<boolean>(false);

  const [downloading, setDownloading] = useState<boolean>(false);
  const [evacuationModalOpen, setEvacuationModalOpen] = useState<boolean>(false);
  const [evacuationPlanData, setEvacuationPlanData] = useState<EvacuationPlanData | null>(null);
  const [teamModalOpen, setTeamModalOpen] = useState<boolean>(false);

  const scrollToTop = () => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  };

  useEffect(() => {
    scrollToTop();
  }, [step]);

  // Auto detect expert info from selected workplace or localStorage
  useEffect(() => {
    if (selectedWorkplace?.assignedExpert) {
      const exp = selectedWorkplace.assignedExpert;
      const fullName = `${exp.firstName || ''} ${exp.lastName || ''}`.trim();
      if (fullName) setExpertName(fullName);
      if (exp.certificateNumber) setExpertCertNo(exp.certificateNumber);
    } else {
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const u = JSON.parse(userStr);
          const name = u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.name || '';
          if (name && !expertName) setExpertName(name);
          if ((u.certificateNumber || u.isgNo) && !expertCertNo) setExpertCertNo(u.certificateNumber || u.isgNo);
        }
      } catch {}
    }
  }, [selectedWorkplace]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getToken = () =>
    localStorage.getItem('fieldToken') ||
    localStorage.getItem('authToken') ||
    localStorage.getItem('token');

  const getHeaders = () => {
    const token = getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const orgId = localStorage.getItem('organizationId');
    if (orgId) headers['x-organization-id'] = orgId;
    return headers;
  };

  useEffect(() => {
    fetchWorkplaces();
  }, []);

  const fetchWorkplaces = async () => {
    try {
      setLoading(true);
      const res = await workplaceApi.getAll();
      const list = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);

      const normalized: Workplace[] = list.map((wp: any) => ({
        id: wp.id,
        name: wp.title || wp.name || `İşyeri #${wp.id}`,
        address: wp.address || [wp.district, wp.city].filter(Boolean).join(' / ') || '',
        riskLevel: wp.dangerClass || wp.riskLevel || 'dangerous',
        sgkNo: wp.sgkNo || wp.sskRegistrationNo || '',
        sskRegistrationNo: wp.sgkNo || wp.sskRegistrationNo || '',
        city: wp.city,
        district: wp.district,
        employeeCount: wp.employeeCount || 1,
        naceCode: wp.naceCode,
        sector: wp.sector,
        phone: wp.contactPhone || wp.phone,
        email: wp.email,
        employerName: wp.contactPerson || wp.employerName
      }));

      setWorkplaces(normalized);
    } catch (err) {
      console.error('Workplaces fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredWorkplaces = workplaces.filter(wp => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const nameMatch = (wp.name || '').toLowerCase().includes(q);
    const sgkMatch = (wp.sskRegistrationNo || wp.sgkNo || '').toLowerCase().includes(q);
    return nameMatch || sgkMatch;
  });

  const handleSelectWorkplace = (wp: Workplace) => {
    setSelectedWorkplace(wp);
    setSearchQuery(wp.name);
    setEmployerName(wp.employerName || '');
    setDropdownOpen(false);

    // Auto calculate validity date based on risk level
    const yearsToAdd = RISK_LEVEL_VALIDITY_YEARS[wp.riskLevel] || 4;
    const today = new Date();
    const valYear = today.getFullYear() + yearsToAdd;
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setValidityDate(`${valYear}-${month}-${day}`);
  };

  const goToStep2 = () => {
    if (!selectedWorkplace) {
      alert('Lütfen bir işyeri seçiniz.');
      return;
    }
    setStep(2);
    scrollToTop();
  };

  const handleAddMember = () => {
    if (!newMember.name.trim()) {
      alert('Lütfen çalışan adı ve soyadını giriniz.');
      return;
    }
    const memberObj: TeamMember = {
      id: String(Date.now()),
      name: newMember.name.trim(),
      role: newMember.role.trim() || 'Ekip Üyesi',
      phone: newMember.phone.trim() || '-'
    };

    setTeams(prev => ({
      ...prev,
      [activeTeamTab]: [...prev[activeTeamTab], memberObj]
    }));

    setNewMember({ name: '', role: 'Ekip Üyesi', phone: '' });
  };

  const handleRemoveMember = (teamKey: string, memberId: string) => {
    setTeams(prev => ({
      ...prev,
      [teamKey]: prev[teamKey].filter(m => m.id !== memberId)
    }));
  };

  const handleAddSurrounding = () => {
    if (!newSurrounding.name.trim()) return;
    const item: SurroundingWorkplace = {
      id: String(Date.now()),
      name: newSurrounding.name.trim(),
      activity: newSurrounding.activity.trim() || '-',
      riskEffect: newSurrounding.riskEffect.trim() || 'Yangın / Patlama Riski'
    };
    setSurroundings(prev => [...prev, item]);
    setNewSurrounding({ name: '', activity: '', riskEffect: '' });
  };

  const handleRemoveSurrounding = (id: string) => {
    setSurroundings(prev => prev.filter(s => s.id !== id));
  };

  const executeWordDownload = async () => {
    if (!wordConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }

    if (!selectedWorkplace) return;
    try {
      setDownloading(true);

      const payload = {
        workplace: {
          id: selectedWorkplace.id,
          name: selectedWorkplace.name,
          employerName: employerName || selectedWorkplace.employerName,
          address: selectedWorkplace.address,
          phone: selectedWorkplace.phone,
          email: selectedWorkplace.email,
          sgkNo: selectedWorkplace.sgkNo || selectedWorkplace.sskRegistrationNo,
          naceCode: selectedWorkplace.naceCode,
          sector: selectedWorkplace.sector,
          riskLevel: selectedWorkplace.riskLevel,
          employeeCount: selectedWorkplace.employeeCount || 1
        },
        issueDate,
        revisionNo,
        validityDate,
        assemblyPoint,
        surroundingWorkplaces: surroundings,
        expert: {
          name: expertName,
          certificateNo: expertCertNo
        },
        doctor: selectedWorkplace.assignedDoctor ? {
          name: `${selectedWorkplace.assignedDoctor.firstName || ''} ${selectedWorkplace.assignedDoctor.lastName || ''}`.trim(),
          diplomaNo: selectedWorkplace.assignedDoctor.diplomaNumber || ''
        } : null,
        employer: {
          name: employerName,
          title: employerTitle
        },
        teams,
        evacuationPlanImage: evacuationPlanData?.imageDataUrl || null
      };

      const blob = await emergencyPlanApi.generateDocx(payload);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeWpName = selectedWorkplace.name.replace(/[^a-zA-Z0-9_\-]/g, '_');
      a.download = `Acil_Durum_Eylem_Plani_${safeWpName}.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setWordConsentOpen(false);
      setWordConsentChecked(false);
    } catch (err: any) {
      alert(err.message || 'Word dosyası indirme sırasında bir hata oluştu');
    } finally {
      setDownloading(false);
    }
  };

  const executePrintPdf = () => {
    if (!pdfConsentChecked) {
      alert('Devam etmek için lütfen İSG mevzuatı onay kutusunu işaretleyiniz.');
      return;
    }
    setPdfConsentOpen(false);
    setPdfConsentChecked(false);
    window.print();
  };

  return (
    <div className="awp-page">
      {/* Top Header */}
      <div className="awp-header">
        <div className="awp-header-left">
          <span className="awp-header-icon">🚨</span>
          <div>
            <h1 className="awp-title">Acil Durum Eylem Planı</h1>
            <p className="awp-subtitle">6331 Sayılı İSG Kanunu Madde 11 Kapsamında Acil Durum Eylem Planı Üretici</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/web/field/documents')}
          style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: '1px solid #1e3a8a',
            borderRadius: '8px',
            padding: '7px 14px',
            fontSize: '11px',
            fontWeight: 800,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(30, 64, 175, 0.25)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.15s ease',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
          onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1.0)'}
        >
          <span>↩</span> Belge Paneline Dön
        </button>
      </div>

      {/* Stepper Bar */}
      <div className="awp-stepper">
        {[
          { num: 1, title: 'İşyeri Seçimi' },
          { num: 2, title: 'Ekip & Detay Girişi' },
          { num: 3, title: 'Belge İndir & PDF' }
        ].map((s) => (
          <div
            key={s.num}
            onClick={() => {
              if (s.num === 1) { setStep(1); scrollToTop(); }
              if (s.num === 2 && selectedWorkplace) { setStep(2); scrollToTop(); }
              if (s.num === 3 && selectedWorkplace) { setStep(3); scrollToTop(); }
            }}
            className={`awp-step ${step === s.num ? 'active' : ''} ${step > s.num ? 'done' : ''}`}
          >
            <div className="awp-step-num">{step > s.num ? '✓' : s.num}</div>
            <div className="awp-step-label">{s.title}</div>
          </div>
        ))}
      </div>

      {/* ── Scroll edilebilir içerik alanı ── */}
      <div className="awp-body">

      {/* STEP 1: İşyeri Seçimi */}
      {step === 1 && (
        <div className="awp-card">
          <h2 className="awp-card-title">İşyeri Seçin</h2>
          <p className="text-xs text-slate-500 mb-4">
            İşyeri ünvanı veya SGK Sicil No girerek arama yapabilirsiniz:
          </p>

          <div className="awp-wp-search-wrap" ref={searchWrapRef}>
            <div className="awp-wp-search-box">
              <span className="awp-wp-search-icon">🔍</span>
              <input
                type="text"
                className="awp-wp-search-input"
                placeholder="İşyeri adı veya SGK Sicil No ile arayın..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setDropdownOpen(true);
                }}
                onFocus={() => setDropdownOpen(true)}
                onClick={() => setDropdownOpen(true)}
                autoComplete="off"
              />
              {searchQuery && (
                <button
                  className="awp-wp-search-clear"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedWorkplace(null);
                    setDropdownOpen(false);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Dropdown Results */}
            {dropdownOpen && (
              <div className="awp-wp-dropdown">
                {loading ? (
                  <div className="awp-wp-dropdown-empty">İşyerleri yükleniyor...</div>
                ) : filteredWorkplaces.length === 0 ? (
                  <div className="awp-wp-dropdown-empty">Eşleşen işyeri bulunamadı.</div>
                ) : (
                  filteredWorkplaces.map((wp) => (
                    <div
                      key={wp.id}
                      className={`awp-wp-dropdown-item ${selectedWorkplace?.id === wp.id ? 'active' : ''}`}
                      onClick={() => handleSelectWorkplace(wp)}
                    >
                      <div className="awp-wp-dropdown-main">
                        <span className="awp-wp-dropdown-name">
                          <WorkplaceNameDisplay name={wp.name} />
                        </span>
                        <span className={`awp-risk-badge awp-risk-${wp.riskLevel}`}>
                          {RISK_LEVEL_LABEL[wp.riskLevel] || wp.riskLevel}
                        </span>
                      </div>
                      <div className="awp-wp-dropdown-sub">
                        <span>SGK: <strong>{wp.sskRegistrationNo || wp.sgkNo || '—'}</strong></span>
                        {(wp.city || wp.district) && <span>{[wp.city, wp.district].filter(Boolean).join(' / ')}</span>}
                        {wp.employeeCount && <span>👥 {wp.employeeCount}</span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Selected Workplace Preview Card */}
            {selectedWorkplace && (
              <div className="awp-wp-preview">
                <div className="awp-wp-preview-grid mb-6">
                  <div className="awp-wp-preview-item">
                    <span className="awp-wp-preview-label">Seçilen Firma</span>
                    <span className="awp-wp-preview-val">{selectedWorkplace.name}</span>
                  </div>
                  <div className="awp-wp-preview-item">
                    <span className="awp-wp-preview-label">SGK Sicil No</span>
                    <span className="awp-wp-preview-val font-mono">{selectedWorkplace.sskRegistrationNo || selectedWorkplace.sgkNo || '—'}</span>
                  </div>
                  <div className="awp-wp-preview-item">
                    <span className="awp-wp-preview-label">Tehlike Sınıfı</span>
                    <div>
                      <span className={`awp-risk-badge awp-risk-${selectedWorkplace.riskLevel}`}>
                        {RISK_LEVEL_LABEL[selectedWorkplace.riskLevel] || selectedWorkplace.riskLevel}
                      </span>
                    </div>
                  </div>
                  <div className="awp-wp-preview-item">
                    <span className="awp-wp-preview-label">Çalışan Sayısı & Adres</span>
                    <span className="awp-wp-preview-val text-xs font-semibold text-slate-800">
                      👥 <strong>{selectedWorkplace.employeeCount || 1} Çalışan</strong> {selectedWorkplace.address ? `| ${selectedWorkplace.address}` : ''}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200 mt-6">
                  <button
                    onClick={goToStep2}
                    className="awp-btn-continue"
                    style={{ marginTop: '0px' }}
                  >
                    Devam Et (Ekip & Detay Girişi) →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: FORM & TEAMS MANAGEMENT */}
      {step === 2 && selectedWorkplace && (
        <div className="eap-step2-container">
          
          {/* Selected Workplace Header Bar (Tek Satır Şık Bilgi Bandı - Saf CSS Flex) */}
          <div style={{
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0d2d6b 100%)',
            color: '#ffffff',
            padding: '9px 16px',
            minHeight: '38px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.22), 0 1px 3px rgba(0,0,0,0.1)',
            border: '2px solid #475569',
            flexWrap: 'nowrap',
            overflow: 'hidden',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', flexWrap: 'nowrap' }}>
              
              {/* 1. İşyeri Adı ve Bitişik Tehlike Sınıfı */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <span style={{ fontSize: '8.5px', fontWeight: 800, color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  SEÇİLEN İŞYERİ :
                </span>
                <span style={{ fontSize: '9px', fontWeight: 800, color: '#ffffff' }}>
                  {selectedWorkplace.name}
                </span>
                <span style={{
                  fontSize: '8.5px',
                  fontWeight: 900,
                  color: '#ffffff',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  lineHeight: '1.2',
                  background: selectedWorkplace.riskLevel === 'low' ? '#059669' :
                              selectedWorkplace.riskLevel === 'dangerous' ? '#d97706' : '#dc2626'
                }}>
                  {RISK_LEVEL_LABEL[selectedWorkplace.riskLevel]}
                </span>
              </div>

              {/* 2. Çalışan Sayısı (Baya bir boşluk: 32px) */}
              <span style={{
                marginLeft: '32px',
                fontSize: '9.5px',
                fontWeight: 700,
                color: '#e2e8f0',
                background: 'rgba(30, 41, 59, 0.85)',
                padding: '2px 8px',
                borderRadius: '4px',
                border: '1px solid #475569',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0
              }}>
                👥 <strong>{selectedWorkplace.employeeCount || 1} Çalışan</strong>
              </span>

              {/* 3. SGK Sicil No (Baya bir boşluk: 32px) */}
              {(selectedWorkplace.sskRegistrationNo || selectedWorkplace.sgkNo) && (
                <span style={{
                  marginLeft: '32px',
                  fontSize: '9.5px',
                  fontFamily: 'monospace',
                  color: '#e2e8f0',
                  background: 'rgba(30, 41, 59, 0.85)',
                  padding: '2px 8px',
                  borderRadius: '4px',
                  border: '1px solid #475569',
                  flexShrink: 0
                }}>
                  SGK: <strong>{selectedWorkplace.sskRegistrationNo || selectedWorkplace.sgkNo}</strong>
                </span>
              )}

              {/* 4. Adres (Baya bir boşluk: 32px) */}
              {selectedWorkplace.address && (
                <span
                  title={selectedWorkplace.address}
                  style={{
                    marginLeft: '32px',
                    fontSize: '9.5px',
                    color: '#e2e8f0',
                    background: 'rgba(30, 41, 59, 0.85)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    border: '1px solid #475569',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '380px',
                    flexShrink: 1
                  }}
                >
                  📍 {selectedWorkplace.address}
                </span>
              )}
            </div>
          </div>

          {/* 4 Ana Kart - 4 Kolonlu Yan Yana Düzen */}
          <div className="eap-step2-row">
            
            {/* KART 1: 1. Doküman & Yetkili Bilgileri */}
            <div className="awp-card border-2 border-slate-300 border-l-[6px] border-l-blue-600 space-y-2 eap-card-compact shadow-md">
                <div className="flex justify-between items-center border-b pb-1 gap-1 flex-nowrap">
                  <h3 className="text-[10px] sm:text-[10.5px] font-black text-slate-800 uppercase tracking-tight flex items-center gap-1 truncate whitespace-nowrap" title="1. Doküman & Yetkili Bilgileri">
                    <span>📅</span> 1. Doküman & Yetkililer
                  </h3>
                  <span className="text-[8.5px] font-bold text-blue-900 bg-blue-50 px-1.5 py-0.2 rounded-full border border-blue-200 flex-shrink-0 whitespace-nowrap">
                    Temel
                  </span>
                </div>

                <div className="eap-form-list">
                  {/* 1. Hazırlama Tarihi */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">🗓️</span>
                      <span className="eap-form-label-text">Hazırlama Tarihi</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="date"
                        value={issueDate}
                        onChange={e => setIssueDate(e.target.value)}
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* 2. Revizyon No */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">🏷️</span>
                      <span className="eap-form-label-text">Revizyon No</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={revisionNo}
                        onChange={e => setRevisionNo(e.target.value)}
                        placeholder="00"
                        className="eap-form-input text-center"
                      />
                    </div>
                  </div>

                  {/* 3. Geçerlilik Tarihi */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">⏳</span>
                      <span className="eap-form-label-text">Geçerlilik ({RISK_LEVEL_VALIDITY_YEARS[selectedWorkplace.riskLevel] || 4} Yıl)</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="date"
                        value={validityDate}
                        onChange={e => setValidityDate(e.target.value)}
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* 4. Toplanma Yeri */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">📍</span>
                      <span className="eap-form-label-text">Acil Toplanma Yeri</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={assemblyPoint}
                        onChange={e => setAssemblyPoint(e.target.value)}
                        placeholder="Bahçe / Otopark Alanı"
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* 5. İSG Uzmanı Adı Soyadı */}
                  <div className="eap-form-row" style={{ borderColor: '#93c5fd', background: '#f0f7ff' }}>
                    <div className="eap-form-label">
                      <span className="eap-form-icon">🛡️</span>
                      <span className="eap-form-label-text text-blue-950">İSG Uzmanı</span>
                      <span className="eap-form-colon text-blue-400">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={expertName}
                        onChange={e => setExpertName(e.target.value)}
                        placeholder="Uzman Adı Soyadı"
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* 6. İşveren / Vekili Adı */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">👤</span>
                      <span className="eap-form-label-text">İşveren / Vekili</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={employerName}
                        onChange={e => setEmployerName(e.target.value)}
                        placeholder="İşveren Adı Soyadı"
                        className="eap-form-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* KART 2: 2. Çevreden Etkileyebilecek İşyerleri (Bağımsız Yarım Kart) */}
              <div className="awp-card border-2 border-slate-300 border-l-[6px] border-l-indigo-600 space-y-2 eap-card-compact shadow-md">
                <div className="flex justify-between items-center border-b pb-1 gap-1 flex-nowrap">
                  <h3 className="text-[10px] sm:text-[10.5px] font-black text-slate-800 uppercase tracking-tight flex items-center gap-1 truncate whitespace-nowrap" title="2. Çevreden Etkileyebilecek İşyerleri">
                    <span>🏬</span> 2. Çevre İşyerleri
                  </h3>
                  <span className="text-[8.5px] font-bold text-slate-600 bg-slate-100 px-1.5 py-0.2 rounded-full border border-slate-300 flex-shrink-0 whitespace-nowrap">
                    {surroundings.length} Ekli
                  </span>
                </div>

                <div className="eap-form-list">
                  {/* İşyeri Ünvanı */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">🏢</span>
                      <span className="eap-form-label-text">İşyeri Ünvanı</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={newSurrounding.name}
                        onChange={e => setNewSurrounding({ ...newSurrounding, name: e.target.value })}
                        placeholder="Örn: Petrol İstasyonu"
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* Faaliyet Konusu */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">⚙️</span>
                      <span className="eap-form-label-text">Faaliyet Konusu</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap">
                      <input
                        type="text"
                        value={newSurrounding.activity}
                        onChange={e => setNewSurrounding({ ...newSurrounding, activity: e.target.value })}
                        placeholder="Örn: Akaryakıt Satışı"
                        className="eap-form-input"
                      />
                    </div>
                  </div>

                  {/* Risk / Etki Türü + Ekle Butonu */}
                  <div className="eap-form-row">
                    <div className="eap-form-label">
                      <span className="eap-form-icon">⚠️</span>
                      <span className="eap-form-label-text">Olası Risk / Etki</span>
                      <span className="eap-form-colon">:</span>
                    </div>
                    <div className="eap-form-input-wrap flex gap-1.5">
                      <input
                        type="text"
                        value={newSurrounding.riskEffect}
                        onChange={e => setNewSurrounding({ ...newSurrounding, riskEffect: e.target.value })}
                        placeholder="Örn: Parlama / Patlama"
                        className="eap-form-input flex-1"
                      />
                      <button
                        type="button"
                        onClick={handleAddSurrounding}
                        style={{
                          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '5px',
                          padding: '2px 8px',
                          height: '21px',
                          fontSize: '9.5px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '3px',
                          boxShadow: '0 1px 4px rgba(37, 99, 235, 0.3)',
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                      >
                        <span>➕</span> Ekle
                      </button>
                    </div>
                  </div>
                </div>

                {/* Eklenen Çevre İşyerleri Listesi */}
                {surroundings.length > 0 && (
                  <div className="space-y-1 max-h-[85px] overflow-y-auto pr-1">
                    {surroundings.map((s) => (
                      <div
                        key={s.id}
                        className="p-1 px-2 bg-white border border-slate-200 rounded flex items-center justify-between gap-1.5 shadow-2xs hover:border-slate-300 transition"
                      >
                        <div className="flex-1 min-w-0 flex items-center gap-1.5 text-[10.5px] overflow-hidden whitespace-nowrap">
                          <span className="font-extrabold text-slate-900 truncate flex-shrink-0">🏢 {s.name}</span>
                          {s.activity && <span className="text-slate-500 truncate flex-shrink-0">| ⚙️ {s.activity}</span>}
                          {s.riskEffect && (
                            <span className="text-amber-800 bg-amber-50 px-1 py-0.2 rounded text-[9.5px] font-bold border border-amber-200 truncate">
                              ⚠️ {s.riskEffect}
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => handleRemoveSurrounding(s.id)}
                          className="py-0.2 px-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded text-[9.5px] font-bold transition border border-red-200 flex items-center gap-0.5 flex-shrink-0 cursor-pointer"
                          title="Sil"
                        >
                          <span>🗑️</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* KART 3: 3. Acil Durum Ekipleri & Görev Dağılımı */}
              <div className="awp-card border-2 border-slate-300 border-l-[6px] border-l-amber-500 space-y-2 eap-card-compact shadow-md">
                <div className="flex justify-between items-center border-b pb-1 gap-1 flex-nowrap">
                  <h3 className="text-[10px] sm:text-[10.5px] font-black text-slate-800 uppercase tracking-tight flex items-center gap-1 truncate whitespace-nowrap" title="3. Acil Durum Ekipleri & Görev Dağılımı">
                    <span>🚨</span> 3. Acil Ekipleri
                  </h3>
                </div>

                {/* Ekiplerin Gerçek Butonları (Tıklanınca Sayfa Üzerine Detay Kartı Modalı Açar) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {[
                    {
                      id: 'sondurme',
                      title: 'Söndürme Ekibi',
                      icon: '🔥',
                      bgColor: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                      shadow: 'rgba(220, 38, 38, 0.25)',
                      count: teams.sondurme.length
                    },
                    {
                      id: 'kurtarma',
                      title: 'Kurtarma Ekibi',
                      icon: '🛟',
                      bgColor: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      shadow: 'rgba(37, 99, 235, 0.25)',
                      count: teams.kurtarma.length
                    },
                    {
                      id: 'koruma',
                      title: 'Koruma Ekibi',
                      icon: '🛡️',
                      bgColor: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                      shadow: 'rgba(124, 58, 237, 0.25)',
                      count: teams.koruma.length
                    },
                    {
                      id: 'ilkyardim',
                      title: 'İlkyardım Ekibi',
                      icon: '🩺',
                      bgColor: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      shadow: 'rgba(5, 150, 105, 0.25)',
                      count: teams.ilkyardim.length
                    },
                  ].map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setActiveTeamTab(t.id as any);
                        setTeamModalOpen(true);
                      }}
                      style={{
                        background: t.bgColor,
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '7px',
                        padding: '7px 11px',
                        fontSize: '10.5px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '6px',
                        boxShadow: `0 2px 6px ${t.shadow}`,
                        width: '100%',
                        minHeight: '32px',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                        <span>{t.icon}</span>
                        <span>{t.title}</span>
                      </span>
                      <span style={{
                        background: 'rgba(255,255,255,0.22)',
                        borderRadius: '999px',
                        padding: '1px 8px',
                        fontSize: '9.5px',
                        fontWeight: '900',
                        flexShrink: 0,
                      }}>
                        {t.count} Üye
                      </span>
                    </button>
                  ))}
                </div>

                {/* Toplam Kadro Özeti Buton / Şerit */}
                <div style={{
                  padding: '3px 8px',
                  background: '#f8fafc',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginTop: '2px'
                }}>
                  <span style={{ fontSize: '8.5px', fontWeight: 700, color: '#475569', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <span>👥</span> Toplam Kadro:
                  </span>
                  <span style={{
                    fontSize: '8.5px',
                    fontWeight: 900,
                    color: '#1e3a8a',
                    background: '#ffffff',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    border: '1px solid #cbd5e1',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                  }}>
                    {teams.sondurme.length + teams.kurtarma.length + teams.koruma.length + teams.ilkyardim.length} Çalışan
                  </span>
                </div>
              </div>

              {/* KART 4: 4. Vaziyet & Tahliye Planı Krokisi (Bağımsız Yarım Kart) */}
              <div className="awp-card border-2 border-slate-300 border-l-[6px] border-l-emerald-600 space-y-2 eap-card-compact shadow-md">
                <div className="flex justify-between items-center border-b pb-1 gap-1 flex-nowrap">
                  <h3 className="text-[10px] sm:text-[10.5px] font-black text-slate-800 uppercase tracking-tight flex items-center gap-1 truncate whitespace-nowrap" title="4. Vaziyet & Tahliye Planı Krokisi">
                    <span>🗺️</span> 4. Tahliye Krokisi
                  </h3>
                  <span className={`text-[8.5px] font-bold px-1.5 py-0.2 rounded-full border flex-shrink-0 whitespace-nowrap ${
                    evacuationPlanData
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
                      : 'bg-slate-100 text-slate-600 border-slate-300'
                  }`}>
                    {evacuationPlanData ? '✓ Hazır' : 'İsteğe Bağlı'}
                  </span>
                </div>

                {!evacuationPlanData ? (
                  <div className="p-2.5 px-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg flex items-center justify-between gap-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-2xl flex-shrink-0">🎨</span>
                      <div className="min-w-0">
                        <h4 className="text-[11px] font-bold text-slate-800 truncate">
                          Vaziyet ve Tahliye Krokisi
                        </h4>
                        <p className="text-[10px] text-slate-500 leading-tight">
                          Yangın tüpleri ve kaçış kapılarını ekranda çizin veya taslak yükleyin.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEvacuationModalOpen(true)}
                      style={{
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '7px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        fontWeight: '800',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        boxShadow: '0 2px 6px rgba(5, 150, 105, 0.25)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}
                    >
                      <span>✏️</span> Kroki Çiz
                    </button>
                  </div>
                ) : (
                  <div className="p-2 px-2.5 bg-emerald-50/70 border border-emerald-300 rounded-lg flex items-center justify-between gap-2.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-14 h-10 bg-white border border-slate-300 rounded overflow-hidden shadow-2xs flex-shrink-0">
                        <img
                          src={evacuationPlanData.imageDataUrl}
                          alt="Tahliye Krokisi Önizleme"
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] font-black text-emerald-950 truncate flex items-center gap-1">
                          <span>✓</span> Kroki Hazır
                        </div>
                        <div className="text-[9.5px] text-slate-600 truncate">
                          {evacuationPlanData.walls.length} Duvar • {evacuationPlanData.symbols.length} Ekipman
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setEvacuationModalOpen(true)}
                        className="px-2 py-1 bg-blue-600 text-white rounded text-[10.5px] font-bold cursor-pointer hover:bg-blue-700"
                      >
                        ✏️ Düzenle
                      </button>
                      <button
                        type="button"
                        onClick={() => setEvacuationPlanData(null)}
                        className="px-2 py-1 bg-red-100 text-red-700 border border-red-300 rounded text-[10.5px] font-bold cursor-pointer hover:bg-red-200"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                )}
              </div>

          </div>

          {/* Canlı Renkli Navigasyon Butonları (Kompakt Yükseklik) */}
          <div style={{ display: 'flex', gap: '12px', width: '100%', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              onClick={() => {
                setStep(1);
                scrollToTop();
              }}
              style={{
                flex: '1 1 0%',
                height: '38px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(239, 68, 68, 0.2)'
              }}
            >
              <span>←</span> İşyeri Seçimi (Geri)
            </button>

            <button
              type="button"
              onClick={() => {
                setStep(3);
                scrollToTop();
              }}
              style={{
                flex: '1 1 0%',
                height: '38px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12.5px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.2)'
              }}
            >
              Devam Et (Belge İndir & PDF) <span>→</span>
            </button>
          </div>


          </div>
        )}

        {/* STEP 3: PREVIEW & EXPORT WITH LEGAL CONSENT (Kompakt Tek Sayfa) */}
        {step === 3 && selectedWorkplace && (
          <div className="max-w-4xl mx-auto">
            <div className="awp-card awp-compact-download" style={{ border: '2px solid #94a3b8', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
              
              {/* Üst Başlık Satırı */}
              <div className="awp-step3-header">
                <div>
                  <span className="awp-step3-sub">SEÇİLEN FİRMA</span>
                  <h2 className="awp-step3-company">{selectedWorkplace.name}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold px-2.5 py-0.5 bg-blue-100 text-blue-900 rounded-full">
                    {RISK_LEVEL_LABEL[selectedWorkplace.riskLevel] || selectedWorkplace.riskLevel}
                  </span>
                  <span className="text-[11px] font-bold px-2.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full">
                    ✓ Acil Durum Planı Hazır
                  </span>
                  {evacuationPlanData && (
                    <span className="text-[11px] font-bold px-2.5 py-0.5 bg-teal-100 text-teal-800 rounded-full">
                      ✓ Vaziyet Krokisi Dahil
                    </span>
                  )}
                </div>
              </div>

              {/* Word ve PDF Tek Satır Açıklama ve Yan Yana Renkli Butonlar */}
              {!wordConsentOpen && !pdfConsentOpen && (
                <div className="awp-step3-desc-row">
                  <div className="awp-step3-desc-left">
                    <span className="awp-step3-desc-icon">🚨</span>
                    <div>
                      <h4 className="awp-step3-desc-title">
                        Acil Durum Eylem Planı Belgesini İndirin
                      </h4>
                      <p className="awp-step3-desc-text">
                        6331 Sayılı Kanun gereğince oluşturulan acil durum ekiplerini ve eylem planını içeren resmi Word (.docx) veya PDF belgesini oluşturun.
                      </p>
                    </div>
                  </div>

                  <div className="awp-step3-btn-row">
                    <button
                      type="button"
                      onClick={() => {
                        setWordConsentOpen(true);
                        setPdfConsentOpen(false);
                      }}
                      className="awp-step3-btn awp-btn-save"
                    >
                      <span>📥</span> Word İndir
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setPdfConsentOpen(true);
                        setWordConsentOpen(false);
                      }}
                      className="awp-step3-btn awp-btn-generate"
                    >
                      <span>🖨️</span> PDF / Yazdır
                    </button>
                  </div>
                </div>
              )}

              {/* Word Onay Formu */}
              {wordConsentOpen && (
                <div className="awp-consent-panel awp-consent-word">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">📥</span>
                    <div>
                      <strong>Word Belgesi İndirme Onayı (.docx)</strong>
                      <p>
                        İşyerine ait acil durum ekipleri, toplanma bölgesi ve eylem prosedürleri Word belgesi olarak üretilecektir. Belgenin mevzuata uygunluğunu onaylayınız.
                      </p>
                    </div>
                  </div>

                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={wordConsentChecked}
                      onChange={(e) => setWordConsentChecked(e.target.checked)}
                    />
                    <span>Yukarıdaki mevzuat beyanını okudum ve kabul ediyorum.</span>
                  </label>

                  <div className="awp-consent-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setWordConsentOpen(false);
                        setWordConsentChecked(false);
                      }}
                      className="awp-consent-cancel"
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      onClick={executeWordDownload}
                      disabled={!wordConsentChecked || downloading}
                      className={`awp-consent-confirm awp-consent-confirm-green ${!wordConsentChecked || downloading ? 'disabled' : ''}`}
                    >
                      <span>📥</span> {downloading ? 'İndiriliyor...' : 'Onayla ve Word İndir'}
                    </button>
                  </div>
                </div>
              )}

              {/* PDF / Yazdır Onay Formu */}
              {pdfConsentOpen && (
                <div className="awp-consent-panel awp-consent-pdf">
                  <div className="awp-consent-title">
                    <span className="awp-consent-icon">🖨️</span>
                    <div>
                      <strong>PDF / Yazdırma Onayı</strong>
                      <p>
                        Acil Durum Eylem Planı doğrudan yazdırılacak veya PDF olarak kaydedilmek üzere yazdırma ekranı açılacaktır.
                      </p>
                    </div>
                  </div>

                  <label className="awp-consent-check">
                    <input
                      type="checkbox"
                      checked={pdfConsentChecked}
                      onChange={(e) => setPdfConsentChecked(e.target.checked)}
                    />
                    <span>Yukarıdaki mevzuat beyanını okudum ve kabul ediyorum.</span>
                  </label>

                  <div className="awp-consent-actions">
                    <button
                      type="button"
                      onClick={() => {
                        setPdfConsentOpen(false);
                        setPdfConsentChecked(false);
                      }}
                      className="awp-consent-cancel"
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      onClick={executePrintPdf}
                      disabled={!pdfConsentChecked}
                      className={`awp-consent-confirm awp-consent-confirm-blue ${!pdfConsentChecked ? 'disabled' : ''}`}
                    >
                      <span>🖨️</span> Onayla ve Yazdır
                    </button>
                  </div>
                </div>
              )}

              {/* İmza Blokları (Koyulaştırılmış Çerçeveler ve Eşit Hizalanmış İmza Satırları) */}
              <div className="awp-step3-sig-section">
                <h4 className="awp-step3-sig-title">
                  BELGE İMZA VE KAŞE BİLGİLERİ
                </h4>
                <div className="awp-step3-sig-grid">
                  
                  {/* Uzman Bloğu */}
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">HAZIRLAYAN (İSG UZMANI)</p>
                    <p className="awp-step3-sig-name">{expertName || 'İSG Uzmanı Atanmamış'}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>

                  {/* İşveren Bloğu (Noktasız, Temiz) */}
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">ONAYLAYAN (İŞVEREN / VEKİLİ)</p>
                    <p className="awp-step3-sig-name">{employerName || 'İşveren / İşveren Vekili'}</p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>

                  {/* Hekim / Ekip Bloğu */}
                  <div className="awp-step3-sig-block">
                    <p className="awp-step3-sig-role">KONTROL (İŞYERİ HEKİMİ)</p>
                    <p className="awp-step3-sig-name">
                      {selectedWorkplace.assignedDoctor
                        ? `${selectedWorkplace.assignedDoctor.firstName || ''} ${selectedWorkplace.assignedDoctor.lastName || ''}`.trim()
                        : 'İşyeri Hekimi / İSG Ekibi'}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">Kaşe / İmza</p>
                  </div>

                </div>
              </div>

              {/* Geri Butonu Satırı */}
              <div className="awp-step3-back-row">
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                    scrollToTop();
                  }}
                  className="awp-btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5"
                >
                  <span>←</span> Ekip & Detay Girişi (Geri)
                </button>
              </div>

            </div>
          </div>
        )}
      </div>
      {selectedWorkplace && (
        <EvacuationPlanModal
          isOpen={evacuationModalOpen}
          onClose={() => setEvacuationModalOpen(false)}
          onSave={(data) => setEvacuationPlanData(data)}
          workplaceName={selectedWorkplace.name}
          assemblyPoint={assemblyPoint}
          initialData={evacuationPlanData}
        />
      )}

      {/* ── ACİL DURUM EKİBİ YÖNETİM MODALI (SAYFA ÜZERİNE AÇILIR DETAY KARTI) ── */}
      {teamModalOpen && (
        <div className="atm-overlay" onClick={() => setTeamModalOpen(false)}>
          <div className="atm-modal" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="atm-header">
              <div className="atm-header-title">
                <span style={{ fontSize: '22px' }}>
                  {activeTeamTab === 'sondurme' ? '🔥' :
                   activeTeamTab === 'kurtarma' ? '🛟' :
                   activeTeamTab === 'koruma' ? '🛡️' : '🩺'}
                </span>
                <div>
                  <div>
                    {activeTeamTab === 'sondurme' ? 'Söndürme Ekibi Kadrosu' :
                     activeTeamTab === 'kurtarma' ? 'Kurtarma Ekibi Kadrosu' :
                     activeTeamTab === 'koruma' ? 'Koruma Ekibi Kadrosu' : 'İlkyardım Ekibi Kadrosu'}
                  </div>
                  <div className="atm-header-sub">6331 İSG Kanunu gereğince ekibe asıl (Lider) ve yedek çalışanları atayın.</div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTeamModalOpen(false)}
                className="atm-close-btn"
                title="Kapat"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div className="atm-body" style={{ padding: '20px', gap: '20px' }}>
              
              {/* Ekip Seçim Sekmeleri (Yan yana, araları açılmış ve şık) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                gap: '12px',
                background: '#f8fafc',
                padding: '8px',
                borderRadius: '12px',
                border: '2px solid #94a3b8',
                boxShadow: '0 2px 8px rgba(15, 23, 42, 0.08)'
              }}>
                {[
                  { id: 'sondurme', title: 'Söndürme', icon: '🔥', count: teams.sondurme.length, bg: '#dc2626', light: '#fef2f2', border: '#f87171' },
                  { id: 'kurtarma', title: 'Kurtarma', icon: '🛟', count: teams.kurtarma.length, bg: '#2563eb', light: '#eff6ff', border: '#60a5fa' },
                  { id: 'koruma', title: 'Koruma', icon: '🛡️', count: teams.koruma.length, bg: '#7c3aed', light: '#f5f3ff', border: '#a78bfa' },
                  { id: 'ilkyardim', title: 'İlkyardım', icon: '🩺', count: teams.ilkyardim.length, bg: '#059669', light: '#ecfdf5', border: '#34d399' }
                ].map(t => {
                  const isActive = activeTeamTab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveTeamTab(t.id as any)}
                      style={{
                        padding: '10px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        border: isActive ? `2px solid ${t.bg}` : '2px solid #cbd5e1',
                        background: isActive ? '#ffffff' : '#f1f5f9',
                        color: isActive ? t.bg : '#64748b',
                        boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.04)'
                      }}
                    >
                      <span style={{ fontSize: '14px' }}>{t.icon}</span>
                      <span style={{ whiteSpace: 'nowrap' }}>{t.title}</span>
                      <span style={{
                        fontSize: '9.5px',
                        padding: '2px 7px',
                        borderRadius: '999px',
                        fontWeight: 900,
                        background: isActive ? t.bg : '#cbd5e1',
                        color: '#ffffff'
                      }}>
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Yeni Üye Ekleme Formu (Geniş & Şık Giriş Kutuları) */}
              <div style={{
                background: '#ffffff',
                border: '2px solid #94a3b8',
                borderRadius: '12px',
                padding: '16px',
                boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
                  <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#1e293b', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <span>➕</span> Yeni Ekip Üyesi Ata
                  </span>
                  <span style={{
                    fontSize: '10px',
                    fontWeight: 800,
                    padding: '3px 10px',
                    borderRadius: '6px',
                    color: '#ffffff',
                    background: activeTeamTab === 'sondurme' ? '#dc2626' :
                                activeTeamTab === 'kurtarma' ? '#2563eb' :
                                activeTeamTab === 'koruma' ? '#7c3aed' : '#059669',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
                  }}>
                    {activeTeamTab === 'sondurme' ? '🔥 Söndürme Ekibi' :
                     activeTeamTab === 'kurtarma' ? '🛟 Kurtarma Ekibi' :
                     activeTeamTab === 'koruma' ? '🛡️ Koruma Ekibi' : '🩺 İlkyardım Ekibi'}
                  </span>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.1fr 1.3fr', gap: '14px', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ fontSize: '10.5px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '5px' }}>
                      Çalışan Adı *
                    </label>
                    <input
                      type="text"
                      value={newMember.name}
                      onChange={e => setNewMember({ ...newMember, name: e.target.value })}
                      placeholder="Örn: Ahmet Yılmaz"
                      style={{
                        width: '100%',
                        height: '34px',
                        padding: '0 10px',
                        background: '#ffffff',
                        border: '2px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '11.5px',
                        fontWeight: 700,
                        color: '#0f172a',
                        outline: 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        boxSizing: 'border-box'
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.15)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '10.5px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '5px' }}>
                      Ekip Görevi *
                    </label>
                    <select
                      value={newMember.role}
                      onChange={e => setNewMember({ ...newMember, role: e.target.value })}
                      style={{
                        width: '100%',
                        height: '34px',
                        padding: '0 8px',
                        background: '#ffffff',
                        border: '2px solid #cbd5e1',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: 800,
                        color: '#0f172a',
                        outline: 'none',
                        cursor: 'pointer',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        boxSizing: 'border-box'
                      }}
                      onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.15)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                    >
                      <option value="Ekip Başı">⭐ Ekip Başı (Lider)</option>
                      <option value="Ekip Üyesi">👤 Ekip Üyesi (Asıl)</option>
                      <option value="Yedek Üye">🔄 Yedek Üye</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '10.5px', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '5px' }}>
                      Telefon No
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="text"
                        value={newMember.phone}
                        onChange={e => setNewMember({ ...newMember, phone: e.target.value })}
                        placeholder="05XX XXX XX XX"
                        style={{
                          width: '100%',
                          height: '34px',
                          padding: '0 8px',
                          background: '#ffffff',
                          border: '2px solid #cbd5e1',
                          borderRadius: '8px',
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          fontWeight: 700,
                          color: '#0f172a',
                          outline: 'none',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                          boxSizing: 'border-box'
                        }}
                        onFocus={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.15)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)'; }}
                      />
                      <button
                        type="button"
                        onClick={handleAddMember}
                        style={{
                          height: '34px',
                          padding: '0 16px',
                          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '8px',
                          fontSize: '11.5px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          boxShadow: '0 3px 8px rgba(37,99,235,0.35)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <span>➕</span> Ata
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Atanan Üyeler Listesi */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1.5px solid #cbd5e1', paddingBottom: '4px' }}>
                  <h4 style={{ fontSize: '11.5px', fontWeight: 900, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    👥 Atanmış Ekip Kadrosu ({teams[activeTeamTab].length} Kişi)
                  </h4>
                </div>

                {teams[activeTeamTab].length === 0 ? (
                  <div style={{
                    padding: '16px',
                    background: '#fffbeb',
                    border: '2px dashed #f59e0b',
                    borderRadius: '10px',
                    textAlign: 'center',
                    color: '#92400e',
                    fontSize: '11px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '16px' }}>⚠️</span>
                    <span>Bu ekip için henüz üye eklenmedi. Yukarıdaki formdan en az 1 lider çalışan ekleyin.</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto', paddingRight: '4px' }}>
                    {teams[activeTeamTab].map(m => {
                      const isLeader = m.role === 'Ekip Başı' || m.role?.includes('Başı');
                      return (
                        <div
                          key={m.id}
                          style={{
                            padding: '10px 14px',
                            borderRadius: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '12px',
                            border: isLeader ? '2px solid #f59e0b' : '2px solid #cbd5e1',
                            background: isLeader ? '#fffbeb' : '#ffffff',
                            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '15px' }}>{isLeader ? '⭐' : '👤'}</span>
                            <span style={{ fontSize: '12px', fontWeight: 800, color: '#0f172a' }}>
                              {m.name}
                            </span>
                            <span style={{
                              fontSize: '10px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: 800,
                              background: isLeader ? '#fde68a' : '#f1f5f9',
                              color: isLeader ? '#92400e' : '#475569',
                              border: isLeader ? '1px solid #fcd34d' : '1px solid #cbd5e1'
                            }}>
                              {m.role || 'Ekip Üyesi'}
                            </span>
                            {m.phone && m.phone !== '-' && (
                              <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace', fontWeight: 600 }}>
                                📞 {m.phone}
                              </span>
                            )}
                          </div>

                          <button
                            type="button"
                            onClick={() => handleRemoveMember(activeTeamTab, m.id)}
                            style={{
                              padding: '5px 12px',
                              background: '#fef2f2',
                              color: '#dc2626',
                              borderRadius: '6px',
                              fontSize: '10.5px',
                              fontWeight: 800,
                              border: '1.5px solid #fca5a5',
                              boxShadow: '0 1px 3px rgba(220, 38, 38, 0.1)',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              flexShrink: 0,
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fee2e2'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fef2f2'}
                            title="Üyeyi Çıkar"
                          >
                            <span>🗑️</span> Çıkar
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="atm-footer">
              <span className="text-xs font-bold text-slate-500">
                Toplam Kadro: <strong className="text-slate-800">{teams.sondurme.length + teams.kurtarma.length + teams.koruma.length + teams.ilkyardim.length} Kişi</strong>
              </span>
              <button
                type="button"
                onClick={() => setTeamModalOpen(false)}
                style={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  fontSize: '12px',
                  fontWeight: '800',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(37,99,235,0.3)',
                }}
              >
                Kaydet & Kapat ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyActionPlanPage;
